/**
 * =============================================================================
 * VisibilitySelector - Seletor único de visibilidade para entidades
 * Versão: 1.0.0 (2026-06-25)
 *
 * Reaproveita a UI da imagem 1 (Ações Rápidas) e estende com a 4ª opção
 * "Por perfil" — libera o registro para todos os usuários que pertencem a
 * um perfil de permissão.
 *
 * Modos:
 *   - private    -> só o criador
 *   - shared     -> criador + usuários selecionados (multiselect de usuários)
 *   - by_profile -> criador + todos os usuários dos perfis selecionados
 *   - public     -> todos os membros do tenant
 *
 * Componente "controlado": pai fornece value + onChange para cada campo.
 * Usado por: Listas, Campanhas, Templates de WhatsApp.
 * =============================================================================
 */
import { Lock, Users, Tags, Globe2 } from 'lucide-react';
import type { EntityVisibility } from '@/hooks/useEntityVisibility';
import { useTenantMembers } from '@/hooks/useTenantMembers';
import { useTenantPermissionProfiles } from '@/hooks/useTenantPermissionProfiles';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { cn } from '@/lib/utils';

interface VisibilitySelectorProps {
  value: EntityVisibility;
  onChange: (value: EntityVisibility) => void;
  allowedUserIds: string[];
  onAllowedUserIdsChange: (ids: string[]) => void;
  allowedProfileIds: string[];
  onAllowedProfileIdsChange: (ids: string[]) => void;
  /** Esconde o título "Visibilidade" quando integrado em um shell maior. */
  hideTitle?: boolean;
  /** Texto descritivo curto exibido abaixo do título. */
  description?: string;
  disabled?: boolean;
}

/** Definição visual de cada opção. */
const OPTIONS: Array<{
  value: EntityVisibility;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}> = [
  {
    value: 'private',
    label: 'Privada',
    description: 'Apenas você pode ver e usar',
    icon: Lock,
  },
  {
    value: 'shared',
    label: 'Compartilhada',
    description: 'Você + usuários selecionados',
    icon: Users,
  },
  {
    value: 'by_profile',
    label: 'Por perfil',
    description: 'Usuários que pertencem aos perfis selecionados',
    icon: Tags,
  },
  {
    value: 'public',
    label: 'Todos',
    description: 'Todos os membros do tenant podem ver e usar',
    icon: Globe2,
  },
];

export default function VisibilitySelector({
  value,
  onChange,
  allowedUserIds,
  onAllowedUserIdsChange,
  allowedProfileIds,
  onAllowedProfileIdsChange,
  hideTitle = false,
  description = 'Defina quem poderá ver e usar este registro.',
  disabled = false,
}: VisibilitySelectorProps) {
  // Carregamento condicional: só baixa listas quando o modo as exige
  const { data: members, isLoading: loadingMembers } = useTenantMembers({
    enabled: value === 'shared',
  });
  const { profiles, isLoading: loadingProfiles } = useTenantPermissionProfiles(
    value === 'by_profile',
  );

  const toggleUser = (id: string) => {
    if (disabled) return;
    onAllowedUserIdsChange(
      allowedUserIds.includes(id)
        ? allowedUserIds.filter((u) => u !== id)
        : [...allowedUserIds, id],
    );
  };

  const toggleProfile = (id: string) => {
    if (disabled) return;
    onAllowedProfileIdsChange(
      allowedProfileIds.includes(id)
        ? allowedProfileIds.filter((p) => p !== id)
        : [...allowedProfileIds, id],
    );
  };

  return (
    <div className="space-y-4">
      {!hideTitle && (
        <div>
          <h3 className="text-base font-semibold">Visibilidade</h3>
          <p className="text-sm text-muted-foreground mt-1">{description}</p>
        </div>
      )}

      {/* Cards selecionáveis (mesmo padrão visual das Ações Rápidas) */}
      <div className="space-y-2">
        {OPTIONS.map((opt) => {
          const Icon = opt.icon;
          const selected = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              disabled={disabled}
              onClick={() => onChange(opt.value)}
              className={cn(
                'w-full flex items-start gap-3 rounded-lg border px-4 py-3 text-left transition-colors',
                'hover:bg-accent/40',
                selected
                  ? 'border-primary ring-1 ring-primary/30 bg-primary/5'
                  : 'border-border',
                disabled && 'opacity-60 cursor-not-allowed',
              )}
            >
              <span
                className={cn(
                  'mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border',
                  selected ? 'border-primary bg-primary' : 'border-muted-foreground/40',
                )}
                aria-hidden
              >
                {selected && <span className="h-2 w-2 rounded-full bg-primary-foreground" />}
              </span>
              <Icon className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
              <span className="min-w-0">
                <span className="block text-sm font-semibold">{opt.label}</span>
                <span className="block text-xs text-muted-foreground mt-0.5">
                  {opt.description}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {/* Multiselect de usuários quando shared */}
      {value === 'shared' && (
        <div className="rounded-lg border bg-muted/20 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Usuários liberados
            </p>
            <Badge variant="secondary" className="text-xs">
              {allowedUserIds.length} selecionado(s)
            </Badge>
          </div>
          {loadingMembers ? (
            <p className="text-xs text-muted-foreground py-2">Carregando usuários…</p>
          ) : (members?.length ?? 0) === 0 ? (
            <p className="text-xs text-muted-foreground py-2">Nenhum membro disponível.</p>
          ) : (
            <ScrollArea className="h-56 pr-2">
              <ul className="space-y-1">
                {(members ?? []).map((m) => {
                  // Nome principal + email secundário (oculta email se igual ao nome)
                  const displayName = m.full_name || m.email || m.user_id.slice(0, 8);
                  const showEmail = m.email && m.email !== m.full_name;
                  return (
                    <li key={m.user_id}>
                      <label className="flex items-center gap-3 rounded-md px-2 py-2 hover:bg-accent/40 cursor-pointer">
                        <Checkbox
                          checked={allowedUserIds.includes(m.user_id)}
                          onCheckedChange={() => toggleUser(m.user_id)}
                          disabled={disabled}
                        />
                        <SmartAvatar
                          src={m.avatar_url}
                          name={m.full_name}
                          email={m.email}
                          size="sm"
                          use3DFallback
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-medium truncate">
                            {displayName}
                          </span>
                          {showEmail && (
                            <span className="block text-xs text-muted-foreground truncate">
                              {m.email}
                            </span>
                          )}
                        </span>
                        {m.is_owner && (
                          <Badge variant="outline" className="text-[10px] shrink-0">
                            dono
                          </Badge>
                        )}
                      </label>
                    </li>
                  );
                })}
              </ul>
            </ScrollArea>
          )}
        </div>
      )}

      {/* Multiselect de perfis quando by_profile */}
      {value === 'by_profile' && (
        <div className="rounded-lg border bg-muted/20 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Perfis de permissão liberados
            </p>
            <Badge variant="secondary" className="text-xs">
              {allowedProfileIds.length} selecionado(s)
            </Badge>
          </div>
          {loadingProfiles ? (
            <p className="text-xs text-muted-foreground py-2">Carregando perfis…</p>
          ) : profiles.length === 0 ? (
            <p className="text-xs text-muted-foreground py-2">
              Nenhum perfil de permissão ativo neste tenant.
            </p>
          ) : (
            <ScrollArea className="h-48 pr-2">
              <ul className="space-y-1">
                {profiles.map((p) => (
                  <li key={p.id}>
                    <label className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-accent/40 cursor-pointer">
                      <Checkbox
                        checked={allowedProfileIds.includes(p.id)}
                        onCheckedChange={() => toggleProfile(p.id)}
                        disabled={disabled}
                      />
                      <span className="text-sm flex-1 truncate">{p.name}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </ScrollArea>
          )}
        </div>
      )}
    </div>
  );
}
